import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import config from '../config';
import PhoneInput from 'react-phone-number-input';
import Modal from 'react-modal';
import 'react-phone-number-input/style.css';
import './Groups.css';
import './GroupForm.css';
import './GroupCard.css';


export interface Contact {
    id?: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
}

export interface Group {
    id?: number;
    name: string;
    contacts: Contact[];
}

interface GroupFormProps {
    onSubmit: (group: Group) => void;
    group?: Group | null;
    editing: boolean;
    toggleModal: () => void; // Add this line
}


const GroupForm: React.FC<GroupFormProps> = ({ onSubmit, group, editing, toggleModal }) => {
    const [name, setName] = useState<string>(group ? group.name : '');
    const [contacts, setContacts] = useState<Contact[]>(group ? group.contacts : [{ firstName: '', lastName: '', phoneNumber: '' }]);

    useEffect(() => {
        // Added this to reset the form when the group changes
        setName(group ? group.name : '');
        setContacts(group ? group.contacts : [{ firstName: '', lastName: '', phoneNumber: '' }]);
    }, [group]);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const groupData: Group = { name, contacts };
        if (group) {
            groupData.id = group.id;  // Include the id if a group is being edited
        }
        onSubmit(groupData);
        // Reset form fields
        setName('');
        setContacts([{ firstName: '', lastName: '', phoneNumber: '' }]);
        toggleModal(); // Close the modal

    };

    const handleContactChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
        const newContacts = [...contacts];
        newContacts[index][event.target.name as keyof Contact] = event.target.value;
        setContacts(newContacts);
    };

    const handlePhoneNumberChange = (index: number, value?: string) => {
        const newContacts = [...contacts];
        newContacts[index].phoneNumber = value || '';
        setContacts(newContacts);
    };

    const addContact = () => {
        setContacts([...contacts, { firstName: '', lastName: '', phoneNumber: '' }]);
    };

    const removeContact = (index: number) => {
        const newContacts = [...contacts];
        newContacts.splice(index, 1);
        setContacts(newContacts);
    };

    return (
        <form onSubmit={handleSubmit} >
            <div className="group-form-container">
            
            {editing ? <p>Editing group: {group ? group.name : ''}</p> : null} {/* Display the name of the group being edited */}
            <label>
                <h3><span className="required-star">*</span> Group Name:</h3>
                <input type="text" value={name} onChange={(event) => setName(event.target.value)} required className="group-form-input" />
            </label>
            {contacts && contacts.map((contact, index) => (
                <div key={contact.id || index}>
                    <label>
                        <span className="required-star">*</span> First Name: 
                        <input type="text" name="firstName" value={contact.firstName} onChange={(event) => handleContactChange(index, event)} required className="group-form-input" />
                    </label>
                    <label>
                        Last Name:
                        <input type="text" name="lastName" value={contact.lastName} onChange={(event) => handleContactChange(index, event)} className="group-form-input" />
                    </label>
                    <label>
                        <span className="required-star">*</span> Phone Number:
                        <PhoneInput
                            placeholder="Enter phone number"
                            value={contact.phoneNumber}
                            onChange={(value) => handlePhoneNumberChange(index, value)}
                            defaultCountry="US"
                            required
                        />
                    </label>
                    <button type="button" onClick={() => removeContact(index)} className="group-form-button">Remove Contact</button>
                </div>
            ))}
            <button type="button" onClick={addContact} className="group-form-button">Add Another Contact To Group</button>
            <button type="submit" className="group-form-button">{editing ? 'Update Group' : 'Create Group'}</button> {/* Change button text based on editing state */}
            </div>
                <p><span className="required-star-text">*Required</span></p>
        </form>
    );
};


const GroupCard: React.FC<{ group: Group; onEdit: () => void; onDelete: () => void; onSelect: () => void; selected: boolean }> = ({ group, onEdit, onDelete, onSelect, selected }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [viewMore, setViewMore] = useState(false); // Add this line to manage the view

    const toggleMenu = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent the click event from propagating to the parent
        setMenuOpen(!menuOpen);
    };

    const toggleViewMore = () => {
        setViewMore(!viewMore); // Toggle the view
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        onEdit();
        setMenuOpen(false); // Close the dropdown
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete();
        setMenuOpen(false); // Close the dropdown
    };

    return (
        <div className={`group-card ${selected ? 'selected' : ''}`} onClick={onSelect}>
            <div className="group-card-header">
                <h2>{group.name}</h2>
                <div className="group-card-menu">
                    <button className="group-card-three-dots-button" onClick={toggleMenu}>...</button>
                    {menuOpen && (
                        <ul>
                            <li onClick={handleEdit}>Edit</li>
                            <li onClick={handleDelete}>Delete</li>
                        </ul>
                    )}
                </div>
            </div>
            <ul className="group-card-contacts">
                {group.contacts.slice(0, viewMore ? group.contacts.length : 3).map((contact) => (
                    <li key={contact.id || contact.phoneNumber}>{contact.firstName} {contact.lastName}</li>
                ))}
                {group.contacts.length > 3 && (
                    <button onClick={toggleViewMore}>
                        {viewMore ? "View Less" : "View More"} {/* Change the button text based on the view state */}
                    </button>
                )}
            </ul>
        </div>
    );
};




interface GroupListProps {
    groups: Group[];
    onEdit: (group: Group) => void;
    onDelete: (id: number) => void;
    selectedGroups: Group[];
    setSelectedGroups: (groups: Group[]) => void;
    setEditing: (editing: boolean) => void; // new prop to set the editing state
    toggleModal: () => void;

}

const GroupList: React.FC<GroupListProps> = ({ groups, onEdit, onDelete, selectedGroups, setSelectedGroups, setEditing, toggleModal }) => {

    const [phoneNumberGroups, setPhoneNumberGroups] = useState<{ [key: string]: Set<number> }>({});

    const handleEditClick = (group: Group) => {
        onEdit(group);
        setEditing(true); // Set editing to true
        toggleModal();
    };

    // Define a state to keep track of the count for each phone number
    const [phoneNumberCount, setPhoneNumberCount] = useState<{ [key: string]: number }>({});

    // Define a function to update the count for a group
    const updatePhoneNumberGroups = (group: Group, add: boolean) => {
        const newPhoneNumberGroups = { ...phoneNumberGroups };
        group.contacts.forEach((contact) => {
            const phoneNumber = contact.phoneNumber;
            if (add) {
                newPhoneNumberGroups[phoneNumber] = newPhoneNumberGroups[phoneNumber] || new Set();
                newPhoneNumberGroups[phoneNumber].add(group.id!);
            } else {
                newPhoneNumberGroups[phoneNumber]?.delete(group.id!);
                if (newPhoneNumberGroups[phoneNumber]?.size === 0) {
                    delete newPhoneNumberGroups[phoneNumber];
                }
            }
        });
        setPhoneNumberGroups(newPhoneNumberGroups);
    };


    // Update the count when a group is selected or deselected
    const handleGroupSelect = (group: Group) => {
        if (selectedGroups.includes(group)) {
            setSelectedGroups(selectedGroups.filter(g => g !== group));
            updatePhoneNumberGroups(group, false); // Remove the group
        } else {
            setSelectedGroups([...selectedGroups, group]);
            updatePhoneNumberGroups(group, true); // Add the group
        }
    };


    // Use the count to determine the phone numbers for the "To phone number line"
    const toPhoneNumbers = Object.keys(phoneNumberGroups);


    return (
        <div className="group-list">
            {groups && groups.map((group) => (
                <GroupCard
                    key={group.id || group.name}
                    group={group}
                    onEdit={() => handleEditClick(group)}
                    onDelete={() => onDelete(group.id!)}
                    onSelect={() => handleGroupSelect(group)}
                    selected={selectedGroups.includes(group)}
                />
            ))}
        </div>
    );
};

interface GroupsProps {
    selectedGroups: Group[];
    setSelectedGroups: (groups: Group[]) => void;
}

const Groups: React.FC<GroupsProps> = ({ selectedGroups, setSelectedGroups }) => {
    const [groups, setGroups] = useState<Group[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
    const [editing, setEditing] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const toggleModal = () => {
        setModalIsOpen(!modalIsOpen);
    };


    useEffect(() => {
        // Fetch groups from the server
        axios.get(`${config.apiUrl}/groups`)
            .then((response) => setGroups(response.data))
            .catch((error) => console.error('Failed to fetch groups:', error));
    }, []);

    const handleCreate = async (group: Group) => {
        try {
            const response = await axios.post(`${config.apiUrl}/groups`, group);
            const newGroup = response.data || { ...group, id: Date.now() }; // Use Date.now() as a temporary id
            setGroups(prevGroups => [...prevGroups, newGroup]);
        } catch (error) {
            console.error('Failed to create group:', error);
        } finally {
            setEditing(false);
        }
    };

    const handleUpdate = async (group: Group) => {
        setEditing(true);
        try {
            const response = await axios.put(`${config.apiUrl}/groups/${group.id}`, group);
            setGroups(prevGroups => prevGroups.map(g => g.id === group.id ? response.data : g));
        } catch (error) {
            console.error('Failed to update group:', error);
        } finally {
            setEditing(false);
        }
    };


    const handleDelete = async (id: number) => {
        try {
            await axios.delete(`${config.apiUrl}/groups/${id}`);
            setGroups(prevGroups => prevGroups.filter(group => group.id !== id));
        } catch (error) {
            console.error('Failed to delete group:', error);
        }
    };

    const customModalStyles = {
        content: {
            backgroundColor: 'var(--background-color)', // Use theme background color
            
        },
    };

    const closeModalButtonStyle: React.CSSProperties = {
        position: 'absolute',
        top: '10px',
        right: '10px',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        textAlign: 'center',
        lineHeight: '30px',
        backgroundColor: 'var(--button-background)',
        color: 'var(--text-color)',
        cursor: 'pointer',
    };

    const handleCreateNewGroup = () => {
        setSelectedGroup(null); // Reset the selected group
        setEditing(false); // Ensure editing mode is off
        toggleModal(); // Open the modal
    };

    return (
        <div className="groups-container">
            <h1>My Friend Groups:</h1>
            <Modal appElement={document.getElementById('root') as HTMLElement} isOpen={modalIsOpen} onRequestClose={toggleModal} style={customModalStyles}>
                <GroupForm onSubmit={selectedGroup ? handleUpdate : handleCreate} group={selectedGroup} editing={editing} toggleModal={toggleModal} />
                <div onClick={toggleModal} style={closeModalButtonStyle}>X</div>
            </Modal>
            <GroupList groups={groups} onEdit={setSelectedGroup} onDelete={handleDelete} selectedGroups={selectedGroups} setSelectedGroups={setSelectedGroups} setEditing={setEditing} toggleModal={toggleModal} />
            <button onClick={handleCreateNewGroup}>Create a Group</button>
        </div>
    );
};

export default Groups;
